import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { ChevronDown, Filter, Loader2, Search, Settings2, User } from "lucide-react";
import type { ProsthesisStatus } from "@bookdental/schemas";
import { useSetPageHeader } from "@/lib/page-header";
import { BotaoExportar } from "@/components/shared/BotaoExportar";
import { valoresDaPlanilha } from "@/lib/colunas-export";
import { COLUMN_LABELS, carregarOcultas, carregarOrdem, colunasVisiveis } from "@/components/production/production-columns";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { STATUS_META, STATUS_ORDER } from "@/components/prosthesis/prosthesis-meta";
import { ProductionStageBoardView } from "@/components/production/ProductionStageBoardView";
import { ProductionLoading, ProductionSpreadsheetView } from "@/components/production/ProductionSpreadsheetView";
import { ProductionOrderDetailDialog } from "@/components/production/ProductionOrderDetailDialog";
import { ProductionServiceDetailDialog } from "@/components/production/ProductionServiceDetailDialog";
import { FluxoDeProducaoDialog } from "@/components/production/FluxoDeProducaoDialog";
import { useResponsavelPorUrl } from "@/components/production/useResponsavelPorUrl";
import { usePermissao } from "@/lib/use-permissao";
import {
  flattenServiceRows,
  type IncomingOrderRow,
  type ProductionViewMode,
  type ServiceRow,
} from "@/components/production/production-types";
import { MobileProductionToolbar } from "@/components/production/mobile/MobileProductionToolbar";
import { MobileProductionFilters } from "@/components/production/mobile/MobileProductionFilters";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { PRODUCAO_TOUR_STEPS } from "@/lib/product-tour/page-tour-steps";
import { usePrazoDoLaboratorio } from "@/lib/prazo-do-laboratorio";

const LS_VIEW_MODE = "bookdental.producao.viewMode";

/** Pedidos Aguardando Confirmação/Recusados não entram em Produção (ver filtro de `allOrders`
 *  abaixo) — por isso essas duas ficam fora das pills de Status, senão apareceriam como opção que
 *  nunca casa com nenhuma linha. */
const PRODUCTION_STATUSES: ProsthesisStatus[] = STATUS_ORDER.filter(
  (s) => s !== "AGUARDANDO_CONFIRMACAO" && s !== "RECUSADO",
);

function loadViewMode(): ProductionViewMode {
  if (typeof window === "undefined") return "estagios";
  const stored = window.localStorage.getItem(LS_VIEW_MODE);
  return stored === "planilha" ? "planilha" : "estagios";
}

/** Chave "sem estágio" pra linhas sem `productionStageId` — mesmo sentinela que o board de
 *  Estágios já usa (`ProductionStageBoardView`), pra ficar consistente entre as duas telas. */
const UNSTAGED_KEY = "unstaged";

function rowMatchesFilters(
  row: ServiceRow,
  activeStatuses: Set<ProsthesisStatus>,
  reworkOnly: boolean,
  activeStages: Set<string>,
): boolean {
  if (activeStatuses.size === 0 && !reworkOnly && activeStages.size === 0) return true;
  if (!row.service) return false;
  if (reworkOnly && !row.service.hasOpenRework) return false;
  if (activeStatuses.size > 0 && !activeStatuses.has(row.service.status)) return false;
  if (activeStages.size > 0 && !activeStages.has(row.service.productionStageId ?? UNSTAGED_KEY)) return false;
  return true;
}

export function Producao() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [dentistFilter, setDentistFilter] = useState("");
  const [clinicFilter, setClinicFilter] = useState("");
  const [statusFilters, setStatusFilters] = useState<Set<ProsthesisStatus>>(new Set());
  const [reworkOnly, setReworkOnly] = useState(false);
  const [stageFilters, setStageFilters] = useState<Set<string>>(new Set());
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [viewMode, setViewMode] = useState<ProductionViewMode>(loadViewMode);
  const [onlyMine, setOnlyMine] = useState(false);
  const { responsavelId, limpar: limparResponsavel } = useResponsavelPorUrl();
  // Guarda só o id, não o objeto — o objeto precisa vir sempre fresco da query (ver allOrders/allRows
  // abaixo), senão o diálogo aberto continua mostrando o status antigo depois de editá-lo nele mesmo.
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  const [detailServiceId, setDetailServiceId] = useState<string | null>(null);
  const segmentContainerRef = useRef<HTMLDivElement>(null);
  const [segmentWidth, setSegmentWidth] = useState<number | undefined>(undefined);
  const [fluxoAberto, setFluxoAberto] = useState(false);
  const podeGerenciar = usePermissao("production.manage");
  // A planilha leva o nome dos pacientes das clínicas: permissão própria, não basta ver a produção.
  const podeExportar = usePermissao("production.export");

  // Largura uniforme = a do maior botão de segmentação — medida no DOM (rótulos variam de tamanho
  // de forma imprevisível com acentos/maiúsculas, então "N caracteres" não é uma boa aproximação).
  useLayoutEffect(() => {
    if (!filtersExpanded || !segmentContainerRef.current) return;
    const widths = Array.from(segmentContainerRef.current.querySelectorAll("button")).map(
      (b) => b.getBoundingClientRect().width,
    );
    if (widths.length) setSegmentWidth(Math.max(...widths));
  }, [filtersExpanded]);

  useTourDaPagina(PRODUCAO_TOUR_STEPS);
  useSetPageHeader(
    {
      title: "Produção",
      subtitle: "Pedidos recebidos das clínicas conectadas",
    },
    [],
  );

  const changeViewMode = (mode: ProductionViewMode) => {
    setViewMode(mode);
    window.localStorage.setItem(LS_VIEW_MODE, mode);
  };

  const toggleStatusFilter = (status: ProsthesisStatus) => {
    setStatusFilters((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  const toggleStageFilter = (key: string) => {
    setStageFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const clearFilters = () => {
    setStatusFilters(new Set());
    setReworkOnly(false);
    setStageFilters(new Set());
  };

  const openOrder = (order: IncomingOrderRow) => {
    setDetailServiceId(null);
    setDetailOrderId(order.id);
  };
  const openService = (row: ServiceRow) => setDetailServiceId(row.service?.id ?? null);

  // Deep link vindo da notificação de retrabalho — abre direto o serviço solicitado. Reage a
  // mudanças em searchParams (não só no mount), pois clicar na notificação já estando nessa
  // página só atualiza a URL, sem remontar o componente.
  useEffect(() => {
    const serviceId = searchParams.get("serviceId");
    if (serviceId) {
      setDetailServiceId(serviceId);
      setSearchParams(
        (prev) => {
          prev.delete("serviceId");
          return prev;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  const listQuery = trpc.prosthesis.incoming.list.useQuery();
  const meQuery = trpc.me.current.useQuery();
  const stagesQuery = trpc.prosthesis.stages.list.useQuery();
  const stages = stagesQuery.data ?? [];
  // Produção é a BANCADA: o que ainda dá trabalho. Fora dela ficam os dois extremos — o que ainda
  // não foi aceito (vive em Pedidos → Pendentes) e o que já acabou.
  //
  // Concluído e Cancelado saíram daqui em 2026-08: pedido pronto não é trabalho, e mantê-lo no
  // quadro só engordava a lista que a bancada varre todo dia. Eles continuam a um clique, nas abas
  // Concluídos e Recusados de /lab/pedidos. Retrabalho num pedido já entregue devolve o status pra
  // EM_PRODUCAO — então ele REAPARECE aqui sozinho, sem ninguém precisar ir buscar.
  const FORA_DA_BANCADA: ProsthesisStatus[] = ["AGUARDANDO_CONFIRMACAO", "RECUSADO", "CONCLUIDO", "CANCELADO"];
  const allOrders = ((listQuery.data ?? []) as IncomingOrderRow[]).filter(
    (o) => !FORA_DA_BANCADA.includes(o.status),
  );
  const prazo = usePrazoDoLaboratorio();

  const allRows = useMemo(() => flattenServiceRows(allOrders), [allOrders]);
  const detailOrder = useMemo(() => allOrders.find((o) => o.id === detailOrderId) ?? null, [allOrders, detailOrderId]);
  const detailRow = useMemo(
    () => allRows.find((r) => r.service?.id === detailServiceId) ?? null,
    [allRows, detailServiceId],
  );

  const dentistOptions = useMemo(
    () => [...new Set(allOrders.map((o) => o.dentistName).filter((v): v is string => !!v))].sort(),
    [allOrders],
  );
  const clinicOptions = useMemo(
    () => [...new Set(allOrders.filter((o) => !o.organization.isPersonal).map((o) => o.organization.name))].sort(),
    [allOrders],
  );

  const searchedOrders = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allOrders.filter((o) => {
      if (dentistFilter && o.dentistName !== dentistFilter) return false;
      if (clinicFilter && o.organization.name !== clinicFilter) return false;
      if (!term) return true;
      return (
        o.patientName.toLowerCase().includes(term) ||
        o.organization.name.toLowerCase().includes(term) ||
        (o.dentistName ?? "").toLowerCase().includes(term) ||
        o.services.some(
          (s) =>
            s.serviceCategory.label.toLowerCase().includes(term) ||
            (s.assignedToUser?.name ?? "").toLowerCase().includes(term),
        )
      );
    });
  }, [allOrders, search, dentistFilter, clinicFilter]);

  const rows = useMemo(() => {
    let flat = flattenServiceRows(searchedOrders);
    if (onlyMine && meQuery.data) {
      flat = flat.filter((row) => row.service?.assignedToUserId === meQuery.data!.id);
    }
    // Recorte por colaborador vindo da Equipe (`?responsavel=`) — mesmo campo do "Meus trabalhos",
    // apontando pra outra pessoa.
    if (responsavelId) {
      flat = flat.filter((row) => row.service?.assignedToUserId === responsavelId);
    }
    if (statusFilters.size === 0 && !reworkOnly && stageFilters.size === 0) return flat;
    return flat.filter((row) => rowMatchesFilters(row, statusFilters, reworkOnly, stageFilters));
  }, [searchedOrders, statusFilters, reworkOnly, stageFilters, onlyMine, meQuery.data, responsavelId]);

  // A planilha deixa esconder e reordenar coluna; o arquivo segue a MESMA configuração, senão
  // exportar devolve uma tabela que não é a que a pessoa arrumou. Lido no CLIQUE (o localStorage
  // não é reativo, então calcular no render entregaria a configuração anterior à última mudança).
  // As colunas de ação — seleção, finalizar, anexo, abrir pedido — não têm valor a exportar e caem
  // fora sozinhas.
  const colunasDaExportacao = () => {
    const valores = valoresDaPlanilha((s) => (STATUS_META as Record<string, { label: string }>)[s]?.label ?? s);
    return colunasVisiveis(carregarOrdem(), carregarOcultas())
      .filter((chave) => valores[chave])
      .map((chave) => ({ titulo: COLUMN_LABELS[chave], valor: valores[chave]! }));
  };

  const nomeDoResponsavel = useMemo(() => {
    if (!responsavelId) return null;
    for (const o of allOrders) {
      for (const s of o.services) {
        if (s.assignedToUserId === responsavelId) return s.assignedToUser?.name ?? "colaborador";
      }
    }
    return "colaborador";
  }, [responsavelId, allOrders]);

  const filterCount = statusFilters.size + (reworkOnly ? 1 : 0) + stageFilters.size;

  return (
    <div className="mx-auto w-full max-w-[1600px]">
      {/* Filtro que veio de outra tela precisa se anunciar e ter saída — senão o quadro parece
          vazio sem explicação e não há como voltar ao completo. */}
      {responsavelId && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-brand-200 bg-brand-50/60 px-3 py-2 text-xs text-brand-800 dark:border-primary/30 dark:bg-primary/10 dark:text-primary">
          <span>
            Mostrando apenas os trabalhos de <strong>{nomeDoResponsavel}</strong>.
          </span>
          <button
            type="button"
            onClick={limparResponsavel}
            className="inline-flex h-7 items-center rounded-md border border-brand-300 px-2 font-semibold transition-colors hover:bg-brand-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-primary/40 dark:hover:bg-primary/20"
          >
            Ver todos
          </button>
        </div>
      )}
      <MobileProductionToolbar
        busca={search}
        onBuscaChange={setSearch}
        apenasMeus={onlyMine}
        onApenasMeusChange={setOnlyMine}
        visao={viewMode}
        onVisaoChange={changeViewMode}
        carregando={listQuery.isFetching}
      />

      <MobileProductionFilters
        statusOpcoes={PRODUCTION_STATUSES.map((s) => ({
          valor: s,
          rotulo: STATUS_META[s].label,
          cor: STATUS_META[s].color,
        }))}
        statusSelecionados={[...statusFilters]}
        onStatusChange={(v) => setStatusFilters(new Set(v as ProsthesisStatus[]))}
        retrabalhoAtivo={reworkOnly}
        onRetrabalhoChange={setReworkOnly}
        estagioOpcoes={[
          { valor: UNSTAGED_KEY, rotulo: "Sem estágio" },
          ...stages.map((s) => ({ valor: s.id, rotulo: s.label, cor: s.colorHex ?? undefined })),
        ]}
        estagiosSelecionados={[...stageFilters]}
        onEstagiosChange={(v) => setStageFilters(new Set(v))}
        dentistas={dentistOptions}
        dentista={dentistFilter}
        onDentistaChange={setDentistFilter}
        clinicas={clinicOptions}
        clinica={clinicFilter}
        onClinicaChange={setClinicFilter}
      />

      <div className="mb-3 hidden flex-col gap-2 md:flex md:flex-row md:flex-wrap md:items-center">
        <div className="relative w-full min-w-0 md:max-w-md md:flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar clínica, paciente, dentista, serviço, responsável..."
            className="h-11 w-full rounded-md border border-zinc-200 bg-white pl-9 pr-3 text-base text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 md:h-9 md:text-sm dark:border-border dark:bg-card dark:text-foreground"
          />
        </div>

        <div className="grid grid-cols-[repeat(auto-fit,minmax(9rem,1fr))] gap-2 md:contents">
        <select
          value={dentistFilter}
          onChange={(e) => setDentistFilter(e.target.value)}
          className="h-11 w-full min-w-0 rounded-md border border-zinc-200 bg-white px-2.5 text-sm text-zinc-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 md:h-9 md:w-auto dark:border-border dark:bg-card dark:text-foreground"
        >
          <option value="">Todos os dentistas</option>
          {dentistOptions.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>

        <select
          value={clinicFilter}
          onChange={(e) => setClinicFilter(e.target.value)}
          className="h-11 w-full min-w-0 rounded-md border border-zinc-200 bg-white px-2.5 text-sm text-zinc-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 md:h-9 md:w-auto dark:border-border dark:bg-card dark:text-foreground"
        >
          <option value="">Todas as clínicas</option>
          {clinicOptions.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        </div>

        <button
          type="button"
          data-tour="producao-meus-trabalhos"
          onClick={() => setOnlyMine((v) => !v)}
          className={cn(
            "inline-flex h-11 items-center justify-center gap-1.5 rounded-md border px-3 text-xs font-semibold md:h-9",
            onlyMine
              ? "border-brand-300 bg-brand-50 text-brand-700 dark:border-primary/40 dark:bg-primary/10 dark:text-primary"
              : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-muted-foreground",
          )}
        >
          <User className="h-3.5 w-3.5" />
          Meus trabalhos
        </button>

        {listQuery.isFetching && <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}

        {podeExportar && (
          <BotaoExportar
            nome="producao"
            titulo="produção"
            planilha="producao"
            quantidade={rows.filter((r) => r.service).length}
            colunas={colunasDaExportacao}
            idDe={(l: any) => l.service.id}
            // Pedido sem serviço vira linha vazia na tela; no arquivo não tem o que exportar.
            buscarLinhas={async () => rows.filter((r) => r.service) as any[]}
            disabled={rows.length === 0}
            className="h-12 w-full justify-center md:ml-auto md:h-9 md:w-auto"
          />
        )}

        <div
          className="flex h-12 w-full items-center rounded-md border border-zinc-200 bg-white p-0.5 md:inline-flex md:h-9 md:w-auto dark:border-border dark:bg-card"
        >
          {(
            [
              { key: "estagios", label: "Estágios" },
              { key: "planilha", label: "Planilha" },
            ] as const
          ).map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => changeViewMode(key)}
              className={
                viewMode === key
                  ? "inline-flex h-11 flex-1 items-center justify-center rounded px-3 text-xs font-semibold bg-brand-600 text-white md:h-8 md:flex-none"
                  : "inline-flex h-11 flex-1 items-center justify-center rounded px-3 text-xs font-medium text-zinc-600 hover:text-zinc-900 md:h-8 md:flex-none dark:text-muted-foreground dark:hover:text-foreground"
              }
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Filtros à esquerda, Configurar à direita — a mesma linha junta o que filtra a lista e o
          que configura o fluxo dela, em vez de espalhar os dois pela página. */}
      <div className="mb-3 hidden items-center justify-between gap-2 md:flex">
        <button
          type="button"
          data-tour="producao-filtros"
          onClick={() => setFiltersExpanded((v) => !v)}
          className="inline-flex h-11 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-xs font-semibold text-zinc-600 hover:bg-zinc-50 md:h-8 dark:border-border dark:bg-card dark:text-muted-foreground dark:hover:bg-white/5"
        >
          <Filter className="h-3.5 w-3.5" />
          Filtros
          {filterCount > 0 && (
            <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-bold text-white">
              {filterCount}
            </span>
          )}
          <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", filtersExpanded && "rotate-180")} />
        </button>

        {/* Configurar o fluxo é escrita — o servidor recusa sem `production.manage`, e o botão
            levava a um diálogo que só sabia dar erro. */}
        {podeGerenciar && (
        <button
          type="button"
          onClick={() => setFluxoAberto(true)}
          aria-label="Fluxo de produção"
          className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-zinc-200 px-2.5 text-xs font-medium text-zinc-600 transition-colors hover:border-zinc-300 hover:bg-zinc-50 hover:text-zinc-900 dark:border-border dark:text-muted-foreground dark:hover:bg-muted dark:hover:text-foreground"
        >
          <Settings2 className="h-3.5 w-3.5" />
          Configurar
        </button>
        )}
      </div>

      <div className="mb-3 hidden md:block">
        {filtersExpanded && (
          <div className="mt-2 flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400 dark:text-muted-foreground">
                  Status:
                </span>
                <SegmentButton
                  label="Todos"
                  active={statusFilters.size === 0 && !reworkOnly && stageFilters.size === 0}
                  onClick={clearFilters}
                />
                {PRODUCTION_STATUSES.map((s) => (
                  <SegmentButton
                    key={s}
                    label={STATUS_META[s].label}
                    color={STATUS_META[s].color}
                    bg={STATUS_META[s].bg}
                    active={statusFilters.has(s)}
                    onClick={() => toggleStatusFilter(s)}
                  />
                ))}
              </div>

              <span className="hidden h-5 w-px shrink-0 bg-zinc-200 dark:bg-border sm:block" />

              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400 dark:text-muted-foreground">
                  Retrabalho:
                </span>
                <SegmentButton
                  label="Com retrabalho aberto"
                  color="#ef4444"
                  bg="#ef44441a"
                  active={reworkOnly}
                  onClick={() => setReworkOnly((v) => !v)}
                />
              </div>
            </div>

            {stages.length > 0 && (
              <div>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-400 dark:text-muted-foreground">
                  Estágio interno
                </p>
                <div
                  ref={segmentContainerRef}
                  className="grid items-start justify-items-start gap-1.5"
                  style={{ gridTemplateColumns: `repeat(auto-fill, ${segmentWidth ?? 120}px)` }}
                >
                  <SegmentButton
                    label="Sem estágio"
                    width={segmentWidth}
                    active={stageFilters.has(UNSTAGED_KEY)}
                    onClick={() => toggleStageFilter(UNSTAGED_KEY)}
                  />
                  {stages.map((s) => (
                    <SegmentButton
                      key={s.id}
                      label={s.label}
                      width={segmentWidth}
                      color={s.colorHex ?? undefined}
                      bg={s.colorHex ? `${s.colorHex}1a` : undefined}
                      active={stageFilters.has(s.id)}
                      onClick={() => toggleStageFilter(s.id)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {listQuery.isLoading ? (
        <div className="rounded-lg border border-zinc-200 bg-white dark:border-border dark:bg-card">
          <ProductionLoading />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-zinc-200 bg-white dark:border-border dark:bg-card">
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm font-medium text-zinc-900 dark:text-foreground">
              {search || filterCount > 0 || dentistFilter || clinicFilter ? "Nenhum pedido encontrado" : "Nenhum pedido recebido ainda"}
            </p>
            <p className="mt-1 text-xs text-zinc-500 dark:text-muted-foreground">
              Pedidos de clínicas conectadas aparecem aqui automaticamente.
            </p>
          </div>
        </div>
      ) : viewMode === "estagios" ? (
        <ProductionStageBoardView rows={rows} prazo={prazo} onOpenService={openService} />
      ) : (
        // No celular a Planilha vira lista de cards direto no fundo da página — a moldura de
        // tabela (borda + fundo branco) é coisa do desktop.
        <div className="md:overflow-hidden md:rounded-lg md:border md:border-zinc-200 md:bg-white dark:md:border-border dark:md:bg-card">
          <ProductionSpreadsheetView
            rows={rows}
            emptyMessage={
              search || filterCount > 0 || dentistFilter || clinicFilter ? "Nenhum pedido encontrado" : "Nenhum pedido recebido ainda"
            }
            onOpenService={openService}
            onOpenOrder={openOrder}
          />
        </div>
      )}

      <ProductionServiceDetailDialog row={detailRow} onClose={() => setDetailServiceId(null)} onOpenOrder={openOrder} />
      <ProductionOrderDetailDialog order={detailOrder} onClose={() => setDetailOrderId(null)} />
      <FluxoDeProducaoDialog aberto={fluxoAberto} onFechar={() => setFluxoAberto(false)} />
    </div>
  );
}

function SegmentButton({
  label,
  color,
  bg,
  width,
  active,
  onClick,
}: {
  label: string;
  color?: string;
  bg?: string;
  width?: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width,
        ...(active ? { backgroundColor: bg ?? "#2457ff1a", color: color ?? "#2457ff" } : undefined),
      }}
      className={cn(
        "shrink-0 whitespace-nowrap rounded-full border px-2.5 py-1 text-center text-[11px] font-semibold uppercase transition-colors",
        active
          ? "border-transparent"
          : "border-zinc-200 bg-white text-zinc-500 hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-muted-foreground dark:hover:bg-muted",
      )}
    >
      {label}
    </button>
  );
}
